/**
 * Sprawl Stack (owner, Sep 28: "a block-stacker") — the falling-blocks game on the TV, the blocks drawn as bits of the city's
 * buildings, lit windows and all. ← → move, ↑ turns, ↓ drops faster, Space drops it home; on a touch screen a tap turns, a swipe
 * moves, a swipe down drops. Full rows go (40 / 100 / 300 / 1200 × the level); a new piece with no room ends it.
 */
import { core, end, pop, rnd, tick, A, D, L, R, U, type Core, type MiniGame } from './core';
import { box, halftone, INK, pops, shout } from './draw';

export const COLS = 10, ROWS = 18;
const CELL = 9, W = 320, H = 180, BX = (W - COLS * CELL) / 2, BY = (H - ROWS * CELL) / 2;
const SHAPES: [number, number][][] = [
  [[0, 1], [1, 1], [2, 1], [3, 1]], // I
  [[1, 0], [2, 0], [1, 1], [2, 1]], // O
  [[1, 0], [0, 1], [1, 1], [2, 1]], // T
  [[1, 0], [2, 0], [0, 1], [1, 1]], // S
  [[0, 0], [1, 0], [1, 1], [2, 1]], // Z
  [[0, 0], [0, 1], [1, 1], [2, 1]], // J
  [[2, 0], [0, 1], [1, 1], [2, 1]], // L
];
const COLORS = ['', '#4ac8e8', '#f2c230', '#b060d8', '#5ac86a', '#e8483a', '#3a6ae0', '#f08a2a'];
const LINE_PTS = [0, 40, 100, 300, 1200];
export interface Piece { k: number; r: number; x: number; y: number }
export interface Stack extends Core { grid: number[]; cur: Piece; next: number; bag: number[]; fall: number; lines: number; level: number; das: number; flash: number }

/** a piece's cells, turned `r` quarter turns (the I in its 4 × 4, the O never, the rest in 3 × 3) */
export function cells(k: number, r: number): [number, number][] {
  let c = SHAPES[k]; if (k === 1) return c;
  const n = k === 0 ? 4 : 3;
  for (let i = 0; i < ((r % 4) + 4) % 4; i++) c = c.map(([x, y]) => [n - 1 - y, x] as [number, number]);
  return c;
}
export function fits(s: Pick<Stack, 'grid'>, p: Piece): boolean {
  for (const [cx, cy] of cells(p.k, p.r)) { const x = p.x + cx, y = p.y + cy; if (x < 0 || x >= COLS || y >= ROWS) return false; if (y >= 0 && s.grid[y * COLS + x]) return false; }
  return true;
}
/** the next kind from a shuffled bag of all seven */
function draw7(s: Stack): number { if (!s.bag.length) { s.bag = [0, 1, 2, 3, 4, 5, 6]; for (let i = 6; i > 0; i--) { const j = Math.floor(rnd(s) * (i + 1)); [s.bag[i], s.bag[j]] = [s.bag[j], s.bag[i]]; } } return s.bag.pop()!; }
function spawn(s: Stack): void { s.cur = { k: s.next, r: 0, x: 3, y: 0 }; s.next = draw7(s); s.fall = 0; if (!fits(s, s.cur)) end(s); }
function lock(s: Stack): void {
  for (const [cx, cy] of cells(s.cur.k, s.cur.r)) { const y = s.cur.y + cy; if (y >= 0) s.grid[y * COLS + s.cur.x + cx] = s.cur.k + 1; }
  let n = 0;
  for (let y = ROWS - 1; y >= 0; y--) {
    let full = true; for (let x = 0; x < COLS; x++) if (!s.grid[y * COLS + x]) { full = false; break; }
    if (full) { s.grid.splice(y * COLS, COLS); s.grid.unshift(...new Array(COLS).fill(0)); n++; y++; }
  }
  if (n) { s.score += LINE_PTS[n] * (s.level + 1); s.lines += n; s.level = Math.floor(s.lines / 10); s.fx.push('line'); s.flash = 0.25; pop(s, W / 2, BY + ROWS * CELL * 0.5, n === 4 ? 'BLOCK PARTY!' : n > 1 ? `${n} ROWS!` : 'ROW!', n === 4 ? '#ff4a8a' : '#ffe14a'); }
  else s.fx.push('click');
  spawn(s);
}
function move(s: Stack, dx: number): boolean { const p = { ...s.cur, x: s.cur.x + dx }; if (!fits(s, p)) return false; s.cur = p; return true; }
function turn(s: Stack): void { for (const kick of [0, -1, 1, -2, 2]) { const p = { ...s.cur, r: (s.cur.r + 1) % 4, x: s.cur.x + kick }; if (fits(s, p)) { s.cur = p; s.fx.push('turn'); return; } } }
export function dropY(s: Stack): number { let y = s.cur.y; while (fits(s, { ...s.cur, y: y + 1 })) y++; return y; }

export const stack: MiniGame<Stack> = {
  id: 'sprawl_stack', name: 'Sprawl Stack', icon: '🧱', color: '#6a3ab0', where: 'tv',
  blurb: 'Falling blocks of the city: fill a row and it goes.',
  keys: '← → move · ↑ turn · ↓ faster · Space drop', touch: 'tap turns · swipe moves · swipe down drops',
  w: W, h: H, maxRate: 600, maxScore: 2000000,
  init(seed) { const s = core(seed) as Stack; Object.assign(s, { grid: new Array(COLS * ROWS).fill(0), bag: [], fall: 0, lines: 0, level: 0, das: 0, flash: 0 }); s.next = draw7(s); spawn(s); return s; },
  step(s, inp, dt) {
    tick(s, dt); if (s.over) return;
    s.flash = Math.max(0, s.flash - dt);
    if (inp.press & L) { move(s, -1); s.das = 0; }
    if (inp.press & R) { move(s, 1); s.das = 0; }
    if (inp.x && !(inp.press & (L | R))) { s.das += dt; if (s.das > 0.2) { s.das -= 0.055; move(s, inp.x < 0 ? -1 : 1); } } else if (!inp.x) s.das = 0;
    if ((inp.press & U) || inp.ptap) turn(s);
    if ((inp.press & A) || (inp.swipe & D)) { const y = dropY(s); s.score += (y - s.cur.y) * 2; s.cur = { ...s.cur, y }; lock(s); return; }
    const soft = inp.y > 0.5 || (inp.press & D) !== 0;
    s.fall += dt;
    const every = soft ? 0.04 : Math.max(0.07, 0.75 - s.level * 0.065);
    while (s.fall >= every && !s.over) {
      s.fall -= every;
      const p = { ...s.cur, y: s.cur.y + 1 };
      if (fits(s, p)) { s.cur = p; if (soft) s.score += 1; } else { lock(s); break; }
    }
  },
  draw(ctx, s) {
    ctx.fillStyle = '#2a1f4a'; ctx.fillRect(0, 0, W, H); halftone(ctx, 0, 0, W, H, 'rgba(255,255,255,0.06)', 8, 1.4);
    // the skyline behind, then the well
    for (let i = 0; i < 12; i++) { const bh = 30 + ((i * 37) % 60), x = i * 28 - 6; ctx.fillStyle = '#3a2d62'; ctx.fillRect(x, H - bh, 24, bh); ctx.fillStyle = 'rgba(255,225,120,0.25)'; for (let wy = H - bh + 5; wy < H - 4; wy += 8) for (let wx = x + 4; wx < x + 20; wx += 7) if (((wx * 7 + wy * 3) % 5) < 2) ctx.fillRect(wx, wy, 3, 4); }
    box(ctx, BX - 3, BY - 3, COLS * CELL + 6, ROWS * CELL + 6, '#141024', 2, 2.5);
    const block = (x: number, y: number, k: number, ghost = false) => {
      const px = BX + x * CELL, py = BY + y * CELL;
      if (ghost) { ctx.strokeStyle = COLORS[k]; ctx.lineWidth = 1; ctx.strokeRect(px + 1, py + 1, CELL - 2, CELL - 2); return; }
      ctx.fillStyle = COLORS[k]; ctx.fillRect(px, py, CELL, CELL); ctx.strokeStyle = INK; ctx.lineWidth = 1.2; ctx.strokeRect(px + 0.5, py + 0.5, CELL - 1, CELL - 1);
      ctx.fillStyle = 'rgba(255,245,190,0.85)'; ctx.fillRect(px + 2.5, py + 2.5, 2, 3); ctx.fillRect(px + 5, py + 2.5, 2, 3); // (two lit windows)
    };
    for (let y = 0; y < ROWS; y++) for (let x = 0; x < COLS; x++) { const k = s.grid[y * COLS + x]; if (k) block(x, y, k); }
    if (!s.over) { const gy = dropY(s); for (const [cx, cy] of cells(s.cur.k, s.cur.r)) if (gy + cy >= 0) block(s.cur.x + cx, gy + cy, s.cur.k + 1, true); for (const [cx, cy] of cells(s.cur.k, s.cur.r)) if (s.cur.y + cy >= 0) block(s.cur.x + cx, s.cur.y + cy, s.cur.k + 1); }
    if (s.flash > 0) { ctx.fillStyle = `rgba(255,255,255,${s.flash * 2})`; ctx.fillRect(BX, BY, COLS * CELL, ROWS * CELL); }
    // the side panels: the score and the level on the left, the next piece on the right
    const RX = BX + COLS * CELL + 18;
    shout(ctx, 'SPRAWL', BX / 2, 34, 18, '#ffe14a'); shout(ctx, 'STACK', BX / 2, 54, 18, '#ff6a8a');
    shout(ctx, 'SCORE', BX / 2, 88, 9, '#c8b8ff'); shout(ctx, `${s.score}`, BX / 2, 102, 15);
    shout(ctx, `ROWS ${s.lines}  ·  LV ${s.level + 1}`, BX / 2, 124, 9, '#c8b8ff');
    shout(ctx, 'NEXT', RX + 20, 30, 10, '#c8b8ff'); box(ctx, RX, 40, 42, 34, '#141024', 3, 2);
    { const cs = cells(s.next, 0), minX = Math.min(...cs.map((c) => c[0])), maxX = Math.max(...cs.map((c) => c[0])), minY = Math.min(...cs.map((c) => c[1])), maxY = Math.max(...cs.map((c) => c[1]));
      const ox = RX + 21 - ((maxX - minX + 1) * CELL) / 2 - minX * CELL - BX, oy = 57 - ((maxY - minY + 1) * CELL) / 2 - minY * CELL - BY;
      ctx.save(); ctx.translate(ox, oy); for (const [cx, cy] of cs) block(cx, cy, s.next + 1); ctx.restore(); }
    pops(ctx, s);
  },
};
